import type { FieldDefect, StoredFieldDefect } from "@client/types/vision";
import { computePolygonMask } from "./regionMask";

/**
 * Half-plane mask for a curtain field defect. Returns a Uint8Array where 1 marks
 * pixels on the chosen (black) side of the INFINITE line through (x1,y1)-(x2,y2).
 * Treating the line as infinite is the auto-extrapolation: a short drawn segment
 * still divides the whole image edge to edge.
 *
 * `side` picks which half is black: "a" = the side where the cross product is
 * >= 0, "b" = the other side.
 */
export function computeHalfPlaneMask(
	x1: number,
	y1: number,
	x2: number,
	y2: number,
	side: "a" | "b",
	width: number,
	height: number,
): Uint8Array {
	const mask = new Uint8Array(width * height);
	const dx = x2 - x1;
	const dy = y2 - y1;
	const blackOnA = side === "a";
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const cross = (x - x1) * dy - (y - y1) * dx;
			if (cross >= 0 === blackOnA) mask[y * width + x] = 1;
		}
	}
	return mask;
}

/**
 * Build the runtime blackout mask for a stored field defect.
 * - Tunnel: 1 OUTSIDE the polygon (everything outside goes black).
 * - Curtain: 1 on the chosen side of the dividing line.
 * Returns null if the stored shape is incomplete (e.g. polygon < 3 points).
 */
export function buildFieldDefectMask(
	defect: StoredFieldDefect,
	width: number,
	height: number,
): Uint8Array | null {
	if (defect.mode === "tunnel" && defect.points && defect.points.length >= 3) {
		const inside = computePolygonMask(defect.points, width, height);
		const mask = new Uint8Array(width * height);
		for (let i = 0; i < mask.length; i++) {
			if (inside[i] !== 1) mask[i] = 1; // invert: black outside the polygon
		}
		return mask;
	}
	if (defect.mode === "curtain" && defect.line) {
		const { x1, y1, x2, y2, side } = defect.line;
		return computeHalfPlaneMask(x1, y1, x2, y2, side, width, height);
	}
	return null;
}

/**
 * Paint every masked pixel pitch black (binary, alpha untouched). Runs after the
 * pre-diplopia filters so the curtain/tunnel sits on top of the filtered face.
 */
export function applyFieldDefectBlackout(
	imageData: ImageData,
	defect: FieldDefect,
): ImageData {
	const mask = defect.mask;
	if (!mask) return imageData;
	const data = imageData.data;
	const total = imageData.width * imageData.height;
	for (let i = 0; i < total; i++) {
		if (mask[i] === 1) {
			const idx = i * 4;
			data[idx] = 0;
			data[idx + 1] = 0;
			data[idx + 2] = 0;
		}
	}
	return imageData;
}
